/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import APIManager from "../../../ApiManager/ApiManager";
import { COMMON_ERROR } from "../../ApplicationDetailsError/ApplicationDetailsErrorTypes";
import {
  FETCH_GLOBAL_ADMIN_FLEX_METADATA_REQUEST,
  FETCH_GLOBAL_ADMIN_FLEX_METADATA_SUCCESS,
  FETCH_GLOBAL_ADMIN_FLEX_METADATA_FAILURE,
  SET_GLOBAL_FLEX_METADATA_CHIP_LIST,
  SET_SUBMISSION_API_DATA,
  SET_SUBMISSION_FLEX_METADATA_IDS,
} from "./GlobalAdminFlexTypes";

export const fetchGlobalAdminFlexMetadataRequest = () => {
  return {
    type: FETCH_GLOBAL_ADMIN_FLEX_METADATA_REQUEST,
  };
};

export const fetchGlobalAdminFlexMetadataSuccess = (flexMetadata) => {
  return {
    type: FETCH_GLOBAL_ADMIN_FLEX_METADATA_SUCCESS,
    payload: flexMetadata,
  };
};

export const fetchGlobalAdminFlexMetadataFailure = (error) => {
  return {
    type: FETCH_GLOBAL_ADMIN_FLEX_METADATA_FAILURE,
    payload: error,
  };
};

export const fetchGlobalAdminFlexMetadata = () => {
  return async function (dispatch) {
    dispatch(fetchGlobalAdminFlexMetadataRequest());
    try {
      const data = await APIManager.getGlobalAdminFlexMetadata();
      dispatch(fetchGlobalAdminFlexMetadataSuccess(data));
    } catch (error) {
      dispatch(fetchGlobalAdminFlexMetadataFailure(error));
      dispatch(commonError(error, FETCH_GLOBAL_ADMIN_FLEX_METADATA_FAILURE));
    }
  };
};

export const createGlobalAdminFlexMetadata = (flexDataPayload) => {
  return async function (dispatch) {
    try {
      await APIManager.createGlobalAdminFlexMetadata(flexDataPayload);
      // Refetch the updated flex metadata after creation
      dispatch(fetchGlobalAdminFlexMetadata());
    } catch (error) {
      dispatch(commonError(error, "CREATE_GLOBAL_ADMIN_FLEX_METADATA_FAILURE"));
    }
  };
};

export const updateGlobalAdminFlexMetadata = (flexDataPayload, id) => {
  return async function (dispatch) {
    try {
      await APIManager.updateGlobalAdminFlexMetadata(flexDataPayload, id);
      // Refetch the updated flex metadata after update
      dispatch(fetchGlobalAdminFlexMetadata());
    } catch (error) {
      dispatch(commonError(error, "EDIT_GLOBAL_ADMIN_FLEX_METADATA_FAILURE"));
    }
  };
};

export const deleteGlobalAdminFlexMetadata = (id) => {
  return async function (dispatch) {
    try {
      await APIManager.deleteGlobalAdminFlexMetadata(id);
      // Refetch the updated flex metadata after deletion
      dispatch(fetchGlobalAdminFlexMetadata());
    } catch (error) {
      dispatch(commonError(error, "DELETE_GLOBAL_ADMIN_FLEX_METADATA_FAILURE"));
    }
  };
};

export const setGlobalFlexMetaDataChipList = (chipList) => ({
  type: SET_GLOBAL_FLEX_METADATA_CHIP_LIST,
  payload: chipList,
});

export const setSubmissionAPIdata = (submissionAPIdata) => ({
  type: SET_SUBMISSION_API_DATA,
  payload: submissionAPIdata,
});

export const setSubmissionFlexMetadataIds = (flexMetadataIds) => ({
  type: SET_SUBMISSION_FLEX_METADATA_IDS,
  payload: flexMetadataIds,
});

export const fetchGlobalFlexMetaDataChipList = (appId, submissionId) => {
  return async function (dispatch, getState) {
    try {
      // Populate the store with latest flex metadata
      // await dispatch(fetchGlobalAdminFlexMetadata());

      // Access flexMetadata from GlobalAdminFlexReducers state slice
      const { globalAdminFlexReducers } = getState();
      const flexMetadataState = globalAdminFlexReducers?.flexMetadata ?? [];

      const submissionAPIdata =
        await APIManager.getGlobalAdminFlexSubmissionAPIData(
          appId,
          submissionId
        );

      const flexMetadataIds = submissionAPIdata.flexMetadata || [];

      const filteredFlexMetaChipList = flexMetadataState.filter((item) =>
        flexMetadataIds.includes(item.id)
      );

      dispatch(setSubmissionAPIdata(submissionAPIdata));

      dispatch(setSubmissionFlexMetadataIds(flexMetadataIds));
      // Save filtered list to reducer
      dispatch(setGlobalFlexMetaDataChipList(filteredFlexMetaChipList));
    } catch (error) {
      dispatch(commonError(error, FETCH_GLOBAL_ADMIN_FLEX_METADATA_FAILURE));
    }
  };
};

export const createFlexMetaChipsToSubmission = (
  appId,
  submissionId,
  doesExist,
  flexMetadataPayload
) => {
  return async function (dispatch, getState) {
    try {
      const { globalAdminFlexReducers } = getState();

      if (doesExist) {
        const flexMetadataIds = [flexMetadataPayload.id];
        dispatch(setGlobalFlexMetaDataChipList(flexMetadataIds));
      } else {
        const res = await APIManager.createGlobalAdminFlexMetadata(
          flexMetadataPayload
        );

        const globalFlexMetaDataChipList =
          globalAdminFlexReducers?.globalFlexMetaDataChipList || [];

        const updatedChipList = [...globalFlexMetaDataChipList, res];

        dispatch(setGlobalFlexMetaDataChipList(updatedChipList));

        const existingFlexMetadataIds =
          globalAdminFlexReducers?.flexMetadataIds || [];

        const updatedFlexMetadataIds = [...existingFlexMetadataIds, res.id];

        const submissionStateData =
          globalAdminFlexReducers?.submissionAPIdata || {};

        const submissionUpdatePayload = {
          ...submissionStateData,
          flexMetadata: updatedFlexMetadataIds,
        };

        dispatch(setSubmissionAPIdata(submissionUpdatePayload));

        dispatch(setSubmissionFlexMetadataIds(updatedFlexMetadataIds));
      }

      // Fetch current submission data to get existing flexMetadataIds

      dispatch(fetchGlobalAdminFlexMetadata());
    } catch (error) {
      dispatch(commonError(error, "ADD_FLEX_META_CHIPS_TO_SUBMISSION_FAILURE"));
    }
  };
};

export const commonError = (error, errorType) => {
  return {
    type: COMMON_ERROR,
    payload: {
      message: error.message,
      errorType: errorType,
    },
  };
};
